// AN EVENT'S OWN CLOCK — the one place that says what an event's time MEANS and how it is spelled
// (busy-my-web#238 and #239, both Saffet in Slack).
//
// A task is DUE and can be LATE. An event is not: it approaches, it happens, and then it is over.
//
//   > "in the timeline and Ongoing the event should show when it is due and due soon with the hours
//   >  and minutes but after the event has passed we should not show an event as overdue as it is an
//   >  event and the time has passed it is just a date and time for a meeting or event after it
//   >  passes it has passed"                                                                  (#238)
//
// So an event never reaches `dueStatus`'s `overdue`. It walks its own ladder instead, the one
// Saffet wrote out on #239:
//
//   In 3 days  →  Tomorrow, 6:45am  →  In 1h 38m  →  Now  →  Ended 38m ago  →  Past · 26 Aug, 6:45am
//
// THE THRESHOLDS (his open question on #239; these are the defaults, recorded here so the answer has
// one place to land if he names different ones):
//
//   ≥ 7 days out          "26 Aug, 6:45am"   — a fortnight away, the date itself is the useful fact
//   2–6 days out          "In 3 days"        — his own wording
//   tomorrow              "Tomorrow, 6:45am"
//   today                 "Today, 6:45am"
//   ≤ 2 hours out         "In 1h 38m"        — the countdown takes over from the day naming
//   from start to end     "Now"              — while it is happening
//   < 1 hour since it ended   "Ended 38m ago"
//   same day, after that      "Ended 3h ago"
//   any later                 "Past · 26 Aug, 6:45am"
//
// Everything above the "Now" line is measured in CALENDAR DAYS through lib/timelineTime.js's
// `dayDistance`, never in 24-hour blocks — 11pm and 1am are two hours apart and belong to different
// days, and every near-midnight bug this rail has had came from measuring the gap in milliseconds.
// The clock words ("6:45am", "26 Aug") are that same module's, so an event's time is spelled the
// way every other timeline instant is.

import { parseUtc, DUE_SOON_MS } from '@/lib/dueStatus'
import { dayAndMonth, shortClock, dayDistance, dayEnd } from '@/lib/timelineTime'

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS

/** When the countdown ("In 1h 38m") takes over from the day naming ("Today, 6:45am"). */
export const EVENT_COUNTDOWN_MS = 2 * HOUR_MS
/** How long "Ended 38m ago" is spelled in minutes before it rolls up to whole hours. */
export const EVENT_ENDED_MS = HOUR_MS

/** How long an event lasts when nobody has said otherwise. */
export const EVENT_DEFAULT_LENGTH_MS = HOUR_MS

/**
 * WHEN AN EVENT IS, before anyone has picked a time (busy-my-web#237).
 *
 * An event is not a task: a task's due date is optional and a task with none is a perfectly good
 * task, but an event that happens at no time is not an event, and the box has always known this —
 * it invented "the next whole hour, for an hour" at SAVE time when the date pane had gone untouched.
 *
 * Which is exactly what Saffet reported: "The event entry dates dont show in the Briefing. Only
 * shows after I save the event." The date existed; it was just decided too late for the Briefing to
 * report it. Deciding it here, once, lets the box seed it the moment event composition starts — so
 * the Briefing carries it from the first keystroke, and what was on screen while composing is the
 * very instant that is saved, rather than one recomputed minutes later against a moved clock.
 */
export function defaultEventTimes(now = Date.now()) {
  const start = new Date(now)
  // The next whole hour, in the viewer's own zone. `setHours` carries the date itself over midnight
  // (23:10 → tomorrow 00:00), which is why the arithmetic is done on the Date and not in millis.
  start.setHours(start.getHours() + 1, 0, 0, 0)
  return { start, end: new Date(start.getTime() + EVENT_DEFAULT_LENGTH_MS) }
}

const toDate = (v) => {
  if (v === null || v === undefined || v === '') return null
  const d = v instanceof Date ? v : parseUtc(v)
  return d && !isNaN(d.getTime()) ? d : null
}

/**
 * THE TWO INSTANTS AN ALL-DAY EVENT IS SAVED WITH (busy-my-web#278, frame 7).
 *
 * Ticking "All day" takes the clock away from the user, so the box has to decide the instants
 * itself — and the app already has one answer to "when is a day over": `dayEnd` (lib/timelineTime.js,
 * the #238/#239 rule that keeps today's all-day event out of "Past · 26 Aug" all day long). This is
 * that rule applied at SAVE time rather than at read time, so what is stored agrees with what every
 * rail then says about it.
 *
 * A span of days keeps both ends: it starts at midnight on the first day and ends when the LAST day
 * does (frame 6's 1 Jul → 5 Jul, ticked all-day, is one five-day event, not five). An end that is
 * missing, unreadable, or earlier than the start collapses to the start's own day.
 *
 * @returns `{ start, end }` as Dates, or null when there is no usable start.
 */
export function allDayTimes(start, end = null) {
  const from = toDate(start)
  if (!from) return null
  const first = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const to = toDate(end)
  const last = to && to.getTime() >= first.getTime() ? to : first
  return { start: first, end: dayEnd(last) }
}

/**
 * The two instants an event is judged against: when it starts and when it is over.
 *
 * A TIMED event is judged against its REAL end, and every surface now hands one over: the Ongoing
 * rail and the story steps have always had `dueEnd`, and since busy-main-bff#18 the global timeline
 * feed carries the event's end as `endUtc` too (globalTimelineFeed.js `toRow`). That is what makes a
 * two-hour meeting read "Now" for the whole two hours instead of for one minute of them.
 *
 * A timed event that STILL arrives with no end is treated as ending the moment it begins: "Now" for
 * the minute either side and past after that, the same grace the rest of the app gives an instant
 * (`dueAmount`'s "now" window). That is no longer the feed's normal shape but it is still reachable
 * — an event the BFF has no end for at all (see `TimedEventEnd` there), a cached payload from before
 * #18, an in-flight event the SmartBox has only just started composing.
 *
 * An ALL-DAY event needs no such gap filled, and must not be given that end. Its start IS local
 * midnight, so ending it there would make it "past" from 00:01 — today's all-day event reading
 * "Past · 26 Aug" all through the day it is on (#239's bounce). It ends when its DAY does, which is
 * a question the app already has one answer to (`dayEnd`, lib/timelineTime.js).
 *
 * @returns `{ start, end }` as Dates, or null when there is no usable start.
 */
export function eventBounds(start, end, { allDay = false } = {}) {
  const from = toDate(start)
  if (!from) return null
  const to = toDate(end)
  if (to && to.getTime() > from.getTime()) return { start: from, end: to }
  return { start: from, end: allDay ? dayEnd(from) : from }
}

/**
 * WHICH STAGE an event is in: 'upcoming' | 'dueSoon' | 'now' | 'past' — and never 'overdue' (#238).
 *
 * The two shared with tasks keep their meaning exactly (`dueSoon` is the next hour, `DUE_SOON_MS`),
 * so an event coming up reads and sorts like everything else on the rail. What is new is the far
 * side of the start: the event is happening, and then it is simply over.
 *
 * `null` when there is no usable date — the caller drops the state rather than guessing one.
 */
export function eventStatus(start, { end = null, now = Date.now(), allDay = false } = {}) {
  const bounds = eventBounds(start, end, { allDay })
  if (!bounds) return null
  const from = bounds.start.getTime()
  const to = bounds.end.getTime()
  // The minute either side of the start is "Now" as well: an event whose row ticks over to
  // "Ended 0m ago" a second after it began is telling the truth in the least useful way there is.
  if (now >= from - MINUTE_MS && now <= to + MINUTE_MS) return 'now'
  if (now > to) return 'past'
  return from - now <= DUE_SOON_MS ? 'dueSoon' : 'upcoming'
}

/** Is this stage one an event has already gone through — at or behind the present moment? */
export function isPastEventStatus(status) {
  return status === 'past' || status === 'now'
}

// "1h 38m" / "38m" — the countdown's own amount. Two units, unlike `dueAmount`'s one, because that
// is exactly what Saffet wrote ("In 1h 38m"): inside two hours the minutes are the whole point.
function countdown(ms) {
  const minutes = Math.floor(ms / MINUTE_MS)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return `${Math.max(minutes, 1)}m`
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

// "6:45am", or the all-day event's own words — an all-day event has no clock to print.
const at = (date, allDay) => (allDay ? 'All day' : shortClock(date))

/**
 * THE LABEL — one string, the whole ladder (#239).
 *
 * The same call serves the global timeline's rows, the Ongoing rail's steps and the story dropdowns,
 * so the three surfaces cannot describe one event three ways.
 *
 * @param start   the event's start instant
 * @param end     its end — the rail's `dueEnd`, the feed row's `endUtc` (busy-main-bff#18); null
 *                only where a timed event genuinely has none, and then `eventBounds` fills it
 * @param now     the ticking clock the rails share (`globalTimeline/now`)
 * @param allDay  an all-day event names the day and says so instead of inventing a 12am
 * @returns the label, or '' when there is no usable date.
 */
export function eventLabel(start, { end = null, now = Date.now(), allDay = false } = {}) {
  const bounds = eventBounds(start, end, { allDay })
  if (!bounds) return ''
  const status = eventStatus(start, { end, now, allDay })
  const from = bounds.start.getTime()
  const to = bounds.end.getTime()

  // …and an all-day event that is happening says WHICH DAY, not "Now": it is happening for the
  // whole of it, so the day is the fact and a bare "Now" would be true of every hour in it. This is
  // also the branch a multi-day all-day span lands in while it is running.
  if (status === 'now') return allDay ? `Today, ${at(bounds.start, allDay)}` : 'Now'

  if (status === 'past') {
    const since = now - to
    // Minutes first, then whole hours for the rest of the day it happened on — and after that the
    // relative wording has stopped being useful and the date itself takes over, which is Saffet's
    // own last step ("Eventually I wouldn't even need the relative warning").
    if (!allDay && since < EVENT_ENDED_MS) return `Ended ${Math.max(Math.floor(since / MINUTE_MS), 1)}m ago`
    if (!allDay && dayDistance(bounds.end, now) === 0) return `Ended ${Math.floor(since / HOUR_MS)}h ago`
    const clock = at(bounds.start, allDay)
    return `Past · ${dayAndMonth(bounds.start)}${allDay ? '' : `, ${clock}`}`
  }

  // Ahead of it. Inside the last two hours the countdown is the fact; further out the day is.
  const until = from - now
  if (!allDay && until <= EVENT_COUNTDOWN_MS) return `In ${countdown(until)}`

  const days = dayDistance(bounds.start, now)
  const clock = at(bounds.start, allDay)
  if (days === 0) return `Today, ${clock}`
  if (days === 1) return `Tomorrow, ${clock}`
  if (days < 7) return `In ${days} days`
  return allDay ? dayAndMonth(bounds.start) : `${dayAndMonth(bounds.start)}, ${clock}`
}

export default eventLabel
